/** Mirrors the backend's default `max_upload_bytes` (50 MiB). */
export const MAX_PDF_BYTES = 50 * 1024 * 1024;

export type PdfFileProblem = 'not-pdf' | 'too-large' | 'empty';

/**
 * Client-side pre-check of a chosen file. The backend is the authority (magic bytes, page
 * limit, text layer); this only stops the obvious mistakes before a 50 MB upload starts.
 */
export const validatePdfFile = (file: File, maxBytes: number = MAX_PDF_BYTES): PdfFileProblem | null => {
  const looksLikePdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!looksLikePdf) return 'not-pdf';
  if (file.size === 0) return 'empty';
  if (file.size > maxBytes) return 'too-large';
  return null;
};

/** `1536000` → `1,5 MB`; uses the given locale's decimal separator. */
export const formatBytes = (bytes: number, locale = 'cs'): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['kB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${units[unit]}`;
};
